/* eslint-disable no-unused-vars */
/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useEffectDebugger, useMemoDebugger } from '@components/Common/Helpers/AwpUseEffectDebugger';
import PropTypes from 'prop-types';

import Chart from 'chart.js/auto';
import { getRelativePosition } from 'chart.js/helpers';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Alert } from 'react-bootstrap';

import { GRAPH_TAB_KEY, INI_START_DATE, INI_END_DATE, GRAPH_DISPLAY_DAYS } from '@awp/constants';

import TableView from '@components/dataTables/TableView';
import { setSeconds, setHours, setMinutes, subDays, format as formatDate } from 'date-fns';
import { isValueEmpty } from '@awp/helpers/utilsReact';

import {
  buildGraphData,
  buildOptions,
  fillObservationData,
  getImageDatasets,
  getRegularDatasets,
  getFrostProbeDatasets,
  getGraphMargin,
  syncronizeObservationData,
  hoverEventPlugin,
  isPositionVisible,
} from './Helper';

import ImageTimelineBlock from '@graphs/Image/TimelineBlock';
import { TemperatureProfileController, TemperatureProfileElement } from '@graphs/HeatMap/temperatureProfile';
import ValidationModal from '@components/ManualValidation/validationModal';
import { PutJson } from '@awp/helpers/fetchReact';
import { REST_API, Permissions, ADDITIONAL_DATA_DIRECTION, FORM_MODE, DATASET_TYPES } from '@awp/constants';
import { hasAccess } from '@awp/components/Authorize';

import {
  fetchAdditionalData,
  fetchData,
  selectWeatherStation,
  selectStartDate,
  selectEndDate,
  selectData,
  getSelectedDatasetsForGraph,
  selectHeatMapObservation,
  selectPrecisionResistorObservation,
} from '@stores/dataVisualizationSlice';

import {
  getSelectedDatasetOptions,
  shouldShowInvalidData,
  getViewableEndDate,
  setViewableEndDate,
  shouldResetGraphViewsZoom,
} from '@stores/graphViewPageSlice';

Chart.register(zoomPlugin, TemperatureProfileController, TemperatureProfileElement);

const Graph = ({ graphId, presentation }) => {
  const dispatch = useDispatch();
  const graphKey = graphId.slice(0, graphId.lastIndexOf('_'));
  const weatherStation = useSelector(selectWeatherStation);
  const startDate = useSelector(selectStartDate);
  const endDate = useSelector(selectEndDate);
  const viewableEndDate = useSelector(getViewableEndDate);
  const showInvalidData = useSelector(shouldShowInvalidData);
  const selectedDatasetOptions = useSelector((state) => getSelectedDatasetOptions(state, graphKey));
  const data = useSelector(selectData);
  const fpData = useSelector(selectData);
  const shouldResetGraphZoom = useSelector(shouldResetGraphViewsZoom);

  const heatMapObservation = useSelector(selectHeatMapObservation);
  const precisionResistorObservation = useSelector(selectPrecisionResistorObservation);

  const selectedDatasets = getSelectedDatasetsForGraph(selectedDatasetOptions, data, heatMapObservation);
  const fpSelectedDatasets = getSelectedDatasetsForGraph(selectedDatasetOptions, fpData, heatMapObservation);

  const regularDatasets = getRegularDatasets(selectedDatasets, precisionResistorObservation);
  const imageDatasets = getImageDatasets(selectedDatasets);
  const frostProbeDatasets = getFrostProbeDatasets(fpSelectedDatasets, precisionResistorObservation);

  const [graphData, setGraphData] = useState([]);
  const [graphOptions, setGraphOptions] = useState([]);

  const setStartDate = (fromDate, displayDays) => {
    const endDate = new Date(fromDate);
    const newStart = setMinutes(
      setHours(subDays(endDate, displayDays), endDate.getHours()),
      endDate.getMinutes(),
    ).getTime();

    return newStart;
  };

  //manual validation section
  const hasWriteGraphManualValidationPermission = hasAccess(Permissions.WriteGraphManualValidation);
  const [mode, setMode] = useState(FORM_MODE.LIST);
  const [mStartDate, setMStartDate] = useState(null);
  const [mEndDate, setMEndDate] = useState(null);
  const [timelineValues, setTimelineValues] = useState({
    min: setStartDate(viewableEndDate.value, GRAPH_DISPLAY_DAYS),
    max: viewableEndDate.value,
    left: 0,
    right: 0,
  });

  const setNewDates = (min, max) => {
    const viewableStart = min; //setMinutes(setHours(subDays(new Date(max), GRAPH_DISPLAY_DAYS), 0), 0).getTime();

    if (viewableStart < startDate.value) {
      dispatch(fetchAdditionalData({ direction: ADDITIONAL_DATA_DIRECTION.BACKWARD }));
    }

    if (max > endDate.value) {
      dispatch(fetchAdditionalData({ direction: ADDITIONAL_DATA_DIRECTION.FORWARD }));
    }

    //max is the viewable end date
    // update the end date and let the useEffect manage the start date
    dispatch(
      setViewableEndDate({
        value: new Date(max).getTime(),
        error: '',
        disabled: false,
      }),
    );
  };

  const panComplete = ({ chart }) => {
    let { min, max } = chart.scales.xAxis;

    setTimelineValues({
      min: min,
      max: max,
      left: getGraphMargin(chart.scales, 'left'),
      right: getGraphMargin(chart.scales, 'right'),
    });

    setNewDates(min, max);
  };

  const zoomComplete = ({ chart }) => {
    let { min, max } = chart.scales.xAxis;

    setTimelineValues({
      min: min,
      max: max,
      left: getGraphMargin(chart.scales, 'left'),
      right: getGraphMargin(chart.scales, 'right'),
    });
    // we need to update the image timeline
    //setNewDates(min, max);
  };

  const filteredDatasets = selectedDatasets
    ? selectedDatasets
        .filter((e) => e.type !== DATASET_TYPES.Image)
        .map((e) => {
          //expand 30 minutes for both sides because timelineValues min and max are not accurate
          const data =
            !e || !e.Data
              ? null
              : e.Data.filter(
                  (item) => item.x >= timelineValues.min - 1800000 && item.x <= timelineValues.max + 1800000,
                );
          return { ...e, Data: data };
        })
    : [];

  //this useEffect rebuilds the graph using updated options/data, it's triggered
  // because the user has changed datasets or toggled the invalid data option
  useMemo(() => {
    if (selectedDatasetOptions.length == 0) {
      //when there are no selected dataset options and a chart canvas already exists it's not cleared/destroyed
      // we should dispose of any graphs here
      let chart = Chart.getChart(graphId);
      //only run this code if a chart has been loaded
      if (chart) {
        chart.data.datasets = []; //clear the chart
        //clearing the scales except x axis
        graphOptions.scales = Object.keys(graphOptions.scales)
          .filter((d) => (!d.startsWith('yAxis') ? d : undefined))
          .reduce((r, k) => ((r[k] = graphOptions.scales[k]), r), {});
        setGraphOptions(graphOptions);

        chart.update();
      }

      //exit out
      return;
    }

    if (regularDatasets == null) return;
    //fix the datasets so that they line up at the start point in case they don't line up
    //const syncData = syncronizeObservationData(regularDatasets, 'x');

    //need to generate the start date here becasue if it was updated by a dispatch
    // that dispatch may not have been completed yet
    let min = setStartDate(viewableEndDate.value, GRAPH_DISPLAY_DAYS);

    let datasets = buildGraphData(regularDatasets, showInvalidData);
    let options = buildOptions(
      regularDatasets,
      graphId,
      min,
      viewableEndDate.value,
      panComplete,
      zoomComplete,
      showInvalidData,
    );

    let fpObs = {};
    let depths = [];

    if (frostProbeDatasets?.length > 0) {
      Object.keys(frostProbeDatasets).map((k) => {
        if (frostProbeDatasets[k].Data) {
          Object.keys(frostProbeDatasets[k].Data).map((d) => {
            const ts = parseFloat(frostProbeDatasets[k].Data[d].x);
            fpObs[ts] = fpObs[ts] ?? [];

            fpObs[ts].push({
              displayFormat: frostProbeDatasets[k].graphConfig.displayFormat,
              isNan: Number.isNaN(frostProbeDatasets[k].Data[d].Value) || frostProbeDatasets[k].Data[d].Value === 'NAN',
              isMissing: !frostProbeDatasets[k].Data[d].Value,
              value: parseFloat(frostProbeDatasets[k].Data[d].Value),
              isValid: !frostProbeDatasets[k].Data[d].IsInvalid,
              elevation: frostProbeDatasets[k].Data[d].Depth / 10,
            });
          });
          depths.push(frostProbeDatasets[k].rawElevation / 10);
        }
      });

      depths.sort((a, b) => b - a);

      let heatMapObs = [];
      Object.keys(fpObs).map((o) => {
        heatMapObs.push([o, fpObs[o]]);
      });

      //filter the data down to the visible range
      heatMapObs = heatMapObs
        .map((f) => {
          if (parseFloat(f[0]) > min && parseFloat(f[0]) < viewableEndDate.value) {
            return f;
          }
        })
        .filter((e) => e !== undefined);

      datasets.push({
        type: 'TemperatureProfile',
        label: 'Frost Probe',
        displayFormat:
          frostProbeDatasets.map((f) => f.graphConfig.displayFormat).filter((v, i, s) => s.indexOf(v) === i)[0] || '',
        collectionInterval:
          frostProbeDatasets.map((f) => f.collectionIntervalMinutes).filter((v, i, s) => s.indexOf(v) === i)[0] || 60,
        legendColor: frostProbeDatasets.map((f) => f.color).filter((v, i, s) => s.indexOf(v) === i)[0] || '',
        unitLabel: frostProbeDatasets.map((f) => f.unitLabel).filter((v, i, s) => s.indexOf(v) === i)[0] || '',
        data: heatMapObs,
        yAxisID: 'yAxis-FP',
        xAxisID: 'xAxis',
        order: Infinity, // Must draw last or it will cover the other datasets
        datasetName: 'Frost Probe',
        yAxisList: null,
      });

      options.scales = {
        ...options.scales,
        'yAxis-FP': {
          position: 'right',
          min: depths[depths.length - 1],
          max: depths[0],
          thermistorDepths: depths,
          showInvalidData: showInvalidData,
          grid: { drawOnChartArea: false },
          title: {
            padding: {
              bottom: 36,
            },
            text: 'Frost Probe (cm)',
            display: true,
          },
        },
      };
    }

    setGraphData(datasets);
    setGraphOptions(options);
  }, [viewableEndDate, showInvalidData, selectedDatasetOptions]);
  ``;

  useEffect(() => {
    if (graphOptions.length == 0 && graphData.length == 0) return;
    // get the graph element and verify it was visible (switching tabs...)
    const graphElm = document.getElementById(graphId);
    if (graphElm !== null) {
      const ctx = graphElm.getContext('2d');
      let chart = Chart.getChart(graphId);

      if (chart) {
        chart.data.datasets = graphData;

        if (!shouldResetGraphZoom) {
          if (chart.isZoomedOrPanned()) {
            // we'll use the current scale (diff between min/max) to keep the same 'zoom'
            graphOptions.scales.xAxis.min =
              viewableEndDate.value - (chart.scales.xAxis.getMinMax().max - chart.scales.xAxis.getMinMax().min);
            graphOptions.scales.xAxis.max = viewableEndDate.value;
          }
        }
        chart.options = graphOptions;
        chart.update();
      } else {
        chart = new Chart(ctx, {
          data: {
            datasets: graphData,
          },
          options: graphOptions,
          plugins: [
            {
              beforeDraw: (chart) => {
                const FPAxis = chart.scales['yAxis-FP'];
                if (FPAxis) {
                  let width = 96;
                  let height = 12;

                  let x = Math.round(FPAxis.right) - 20;
                  let y = Math.round(FPAxis.top + FPAxis.bottom - width) / 2;

                  let tickStart = height - 4;
                  let tickEnd = height + 4;

                  //let points = [1, 50, 95];
                  let linePoints = [16, 48, 80];
                  let textPoints = [linePoints[0] - 12, linePoints[1] - 8, linePoints[2] - 12];

                  var ctx = chart.ctx;
                  ctx.save();

                  ctx.translate(x, y);
                  ctx.rotate(Math.PI / 2);

                  var gradient = ctx.createLinearGradient(0, 0, width, 0);
                  gradient.addColorStop(0, '#FEE918');
                  gradient.addColorStop(0.2, '#FEE918');
                  gradient.addColorStop(0.5, '#37B47B');
                  gradient.addColorStop(0.8, '#460056');
                  gradient.addColorStop(1, '#460056');
                  ctx.fillStyle = gradient;
                  ctx.fillRect(0, 0, width, height);

                  ctx.beginPath();
                  ctx.moveTo(0, height);
                  ctx.lineTo(width - 2, height);
                  ctx.stroke();

                  linePoints.map((point) => {
                    ctx.beginPath();
                    ctx.moveTo(point, tickStart);
                    ctx.lineTo(point, tickEnd);
                    ctx.stroke();
                  });

                  ctx.fillStyle = 'black';
                  ctx.font = 'bold 10px Arial';
                  ctx.fillText('+1°C', textPoints[0], 28);
                  ctx.fillText('0°C', textPoints[1], 28);
                  ctx.fillText('-1°C', textPoints[2], 28);
                  ctx.restore();
                }
              },
            },
          ],
        });
      }

      setTimelineValues({
        min: chart.scales.xAxis.min,
        max: chart.scales.xAxis.max,
        left: getGraphMargin(chart.scales, 'left'),
        right: getGraphMargin(chart.scales, 'right'),
      });
    }
  }, [graphData, graphOptions]);

  const OpenValidationModal = () => {
    if (!weatherStation || isValueEmpty(weatherStation.Id)) return;
    if (!selectedDatasets || selectedDatasets.length === 0) return;
    setMode(FORM_MODE.EDIT);
  };

  const setManuValidationDates = () => {
    const chart = Chart.getChart(graphId);
    const { min, max } = chart.scales.xAxis.getMinMax();
    setMStartDate(new Date(min).getTime());
    setMEndDate(new Date(max).getTime());
  };

  const doubleClickHandler = () => {
    setManuValidationDates();
    OpenValidationModal();
  };

  const postValidationSubmit = (isSubmitted) => {
    if (isSubmitted) dispatch(fetchData({ forceFetch: true }));
  };

  return (
    <>
      <div
        style={{
          display:
            GRAPH_TAB_KEY.TABLE_VIEW !== presentation && GRAPH_TAB_KEY.FROST_PROBE !== presentation ? '' : 'none',
        }}
      >
        {/*<progress id={`animationProgress-${graphId}`} max="2000" value="0" style={{ width: '100%' }}></progress>*/}
        <div>
          <canvas
            id={graphId}
            height={250}
            key={graphId}
            //options={graphOptions}
            data={{
              datasets: graphData,
            }}
            //onDoubleClick={hasWriteGraphManualValidationPermission ? doubleClickHandler : () => {}}
          />
        </div>
        <div>
          <ImageTimelineBlock
            imageDatasets={getImageDatasets(imageDatasets)}
            graphId={graphId}
            timelineValues={timelineValues}
          />
        </div>
      </div>
      <div style={{ display: GRAPH_TAB_KEY.TABLE_VIEW === presentation ? '' : 'none' }}>
        <TableView
          selectedDatasets={filteredDatasets}
          id={1}
          key={graphId}
          showInvalidData={showInvalidData}
          showConfidenceLevel={true}
          showValidData={true}
          pageSize={10}
          tableParentClasses={'obsTable obsTable-table tableView overflow-auto table-sm'}
          paginationBarClasses={'obsTable pagination'}
        />
      </div>
      {weatherStation && (
        <ValidationModal
          weatherStation={weatherStation}
          dataSets={selectedDatasets}
          mode={mode}
          setMode={setMode}
          startDate={mStartDate}
          endDate={mEndDate}
          postValidationSubmit={postValidationSubmit}
        />
      )}
    </>
  );
};

Graph.propTypes = {
  graphId: PropTypes.any.isRequired,
  presentation: PropTypes.string,
};

export default Graph;
